import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
    UsersApi,
    GetUsersMeExpandEnum,
} from "@genesys/cloud-frontend/apis/UsersApi.js";
import { UserMe } from "@genesys/cloud-frontend/models/UserMe";
import { RootState } from "app/store";
import QualityFormPermission from "enums/qualityFormPermission";
import { API_CONFIGURATION } from "util/apiUtils";

export type CurrentUserState = {
    status: "idle" | "pending" | "finished" | "failed";
    data?: UserMe;
};

export const fetchUserWithPermissions = createAsyncThunk(
    "auth/userWithPermissions",
    async () => {
        const userApi = new UsersApi(API_CONFIGURATION);
        return userApi.getUsersMe({
            expand: [
                GetUsersMeExpandEnum.Authorization,
                GetUsersMeExpandEnum.Organization,
            ],
        });
    }
);

export const slice = createSlice({
    name: "auth",
    initialState: {
        status: "idle",
    } as CurrentUserState,
    reducers: {
        removeUserPermission(
            state,
            { payload }: { payload: { permissionName: string } }
        ) {
            if (state?.data?.authorization?.permissions !== undefined) {
                const index = state.data.authorization.permissions.findIndex(
                    (permission) => permission === payload.permissionName
                );
                if (index !== -1) {
                    state.data.authorization.permissions.splice(index, 1);
                }
            }
        },
    },
    extraReducers(builder) {
        builder
            .addCase(fetchUserWithPermissions.pending, (state) => {
                state.status = "pending";
            })
            .addCase(
                fetchUserWithPermissions.fulfilled,
                (state, { payload }) => {
                    state.status = "finished";
                    if (payload.authorization?.permissions) {
                        const trimmedPermissions =
                            payload.authorization.permissions.map(
                                (permission) => {
                                    const splitPerms = permission.split(":");
                                    return splitPerms.length > 3
                                        ? splitPerms.slice(0, 3).join(":")
                                        : permission;
                                }
                            );
                        payload.authorization.permissions.length = 0;
                        payload.authorization.permissions.push(
                            ...trimmedPermissions
                        );
                    }
                    state.data = payload;
                }
            )
            .addCase(fetchUserWithPermissions.rejected, (state) => {
                state.status = "failed";
            });
    },
});

export const selectAuthorization = (state: RootState) =>
    state.currentUser.data?.authorization;

export const selectIsAuthenticated = (state: RootState) =>
    !!state.currentUser.data;

export const selectPermission = (state: RootState, permission: string) =>
    !!selectAuthorization(state)?.permissions?.includes(permission);

export const selectCanUserViewSTATopics = (state: RootState) => {
    return selectPermission(state, QualityFormPermission.STA_TOPIC_VIEW);
};

export const selectCanUserViewTranscripts = (state: RootState) => {
    return selectPermission(state, QualityFormPermission.STA_TRANSCRIPT_VIEW);
};

export const selectCanUserViewRecording = (state: RootState) => {
    return (
        selectPermission(state, QualityFormPermission.RECORDING_VIEW) ||
        selectPermission(state, QualityFormPermission.RECORDING_SEGMENT_VIEW)
    );
};

export const selectCanUserViewSensitiveData = (state: RootState) => {
    return selectPermission(
        state,
        QualityFormPermission.QUALITY_EVAL_VIEW_SENSITIVE_DATA
    );
};
export const selectCanUserViewAiScoringExplanation = (state: RootState) => {
    return (
        selectCanUserViewTranscripts(state) && selectCanUserViewRecording(state)
    );
};

export const selectHasQualityEvaluationSearchAny = (state: RootState) => {
    return selectPermission(
        state,
        QualityFormPermission.QUALITY_EVAL_SEARCH_ANY
    );
};

export const selectCanViewScoringRule = (state: RootState) =>
    selectPermission(state, QualityFormPermission.QUALITY_SCORING_RULE_VIEW);

export const selectCanAddScoringRule = (state: RootState) =>
    selectPermission(state, QualityFormPermission.QUALITY_SCORING_RULE_ADD);

export const selectCanEditScoringRule = (state: RootState) =>
    selectPermission(state, QualityFormPermission.QUALITY_SCORING_RULE_EDIT);

export const selectCanDeleteScoringRule = (state: RootState) =>
    selectPermission(state, QualityFormPermission.QUALITY_SCORING_RULE_DELETE);

export const selectCurrentOrgId = (state: RootState) =>
    state.currentUser.data?.organization?.id;

export const selectUser = (state: RootState) => state.currentUser.data;

export const selectStatus = (state: RootState) => state.currentUser.status;

export const currentUserActions = slice.actions;

export default slice.reducer;
